import { useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { graphql } from 'app/generated/gql'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { UserJson } from 'app/types/entities'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useUser } from 'app/utils'
import { setCookie } from 'lib/setCookie.client'
import { useUserStore } from 'app/stores/StoreContext'
import { gqlMutation } from 'app/lib/graphqlClient'

const IMPERSONATE_USER_MUTATION = graphql(`
  mutation ImpersonateUser($userId: String!) {
    impersonateUser(userId: $userId) {
      accessToken
    }
  }
`)

const RESET_PASSWORD_MUTATION = graphql(`
  mutation ResetPassword($email: String!) {
    resetPassword(email: $email)
  }
`)

export type UseUserProfileReturn = {
  user: UserJson
  isSuperUser: boolean
  isDirtyForm: boolean
  setIsDirtyForm: (v: boolean) => void
  isImpersonating: boolean
  impersonateUserHandler: () => void
  resetPasswordHandler: () => void
  onSave: (body: Record<string, unknown>) => void
}

export function useUserProfile(): UseUserProfileReturn {
  const me = useUser()

  const navigate = useNavigate()
  const { userId } = useParams()
  const [confirm] = useConfirmation()
  const [isImpersonating, setIsImpersonating] = useState(false)
  const [isDirtyForm, setIsDirtyForm] = useState(false)
  const [isSuperUser, setIsSuperUser] = useState(false)

  const userStore = useUserStore()

  useEffect(() => {
    if (!userId) return
    userStore.fetchOne(userId).then((result) => {
      if (result.data) {
        const data = result.data as any
        setIsSuperUser(data.isSuperUser ?? me?.type === `super`)
      }
    })
  }, [userId])

  const user = (userStore.current ?? {}) as UserJson

  const onSave = (body: Record<string, unknown>) => {
    const input = {
      name: typeof body.name === 'string' ? body.name : undefined,
      email: typeof body.email === 'string' ? body.email : undefined,
      phone: typeof body.phone === 'string' ? body.phone : undefined,
    }
    userStore.update(userId!, input).then((result) => {
      const res = (result.data as any)?.updateUser
      if (!result.error && res) {
        notifySuccess(`Profile saved`)
        setIsDirtyForm(false)
        navigate(`/settings/users`)
      } else if (result.error) {
        notifyError(result.error.message)
      }
    })
  }

  const impersonateUserHandler = () => {
    if (!userId) return
    confirm({
      title: `Impersonate User`,
      description: `Are you sure you want to impersonate this user? Be responsible with your actions.`,
      variant: `danger`,
    })
      .then(() => setCookie(`hidePhi`, false))
      .then(() => {
        setIsImpersonating(true)
        return gqlMutation(IMPERSONATE_USER_MUTATION, { userId })
      })
      .then((result) => {
        if (result.error) {
          notifyError(result.error.message)
          setIsImpersonating(false)
          return
        }
        if (result.data?.impersonateUser?.accessToken) {
          window.location.href = `/`
        } else {
          notifyError(`Impersonation failed: no token received`)
          setIsImpersonating(false)
        }
      })
      .catch((err) => {
        setIsImpersonating(false)
        if (err?.message) notifyError(err.message)
      })
  }

  const resetPasswordHandler = () => {
    if (!user.email) return
    gqlMutation(RESET_PASSWORD_MUTATION, { email: user.email })
      .then((result) => {
        if (result.error) {
          notifyError(result.error.message)
        } else {
          notifySuccess(`Password reset email sent`)
        }
      })
      .catch(() => notifyError(`Failed to send password reset`))
  }

  return {
    user,
    isSuperUser,
    isDirtyForm,
    setIsDirtyForm,
    isImpersonating,
    impersonateUserHandler,
    resetPasswordHandler,
    onSave,
  }
}
